/**
 * Library Contract — The typed API contract for the Library domain.
 *
 * The library is the user's intelligent research collection:
 * documents, collections, topics, knowledge clusters, and AI extraction.
 *
 * BACKEND ALIGNMENT:
 * - Follows RFC 9457 ProblemDetail for errors
 * - Supports cursor + offset pagination with HATEOAS links
 * - All timestamps are ISO 8601 strings
 *
 * MIGRATION NOTE:
 * UI-only: No generated equivalents exist for library schemas.
 * The library domain schemas (AuthorSchema, DocumentMetaSchema, AISummarySchema,
 * CitationSchema, DocumentRelationshipSchema, LibraryDocumentDTOSchema,
 * LibraryCollectionDTOSchema) are frontend-specific and remain hand-written.
 */

import { z } from 'zod';
import type { PaginatedResponse, PaginationParams, SortParams, ApiResponse } from './shared';

// ─── Enums ─────────────────────────────────────────────────────────────

export const DocumentTypeEnum = z.enum([
  'paper',
  'article',
  'preprint',
  'thesis',
  'report',
  'book',
  'note',
]);
export type DocumentType = z.infer<typeof DocumentTypeEnum>;

export const ProcessingStatusEnum = z.enum([
  'uploading',
  'parsing',
  'extracting',
  'indexing',
  'ready',
  'error',
]);
export type ProcessingStatus = z.infer<typeof ProcessingStatusEnum>;

// Internal — same color values as collections domain but as a Zod schema
// for use in LibraryCollectionDTOSchema validation. Not re-exported.
const CollectionColorEnum = z.enum([
  'neutral',
  'blue',
  'violet',
  'emerald',
  'amber',
  'rose',
  'cyan',
]);
type CollectionColor = z.infer<typeof CollectionColorEnum>;

// ─── DTOs ──────────────────────────────────────────────────────────────

export const AuthorSchema = z.object({
  name: z.string(),
  affiliation: z.string().optional(),
  orcid: z.string().optional(),
});
export type Author = z.infer<typeof AuthorSchema>;

export const DocumentMetaSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  authors: z.array(AuthorSchema),
  abstract: z.string().optional(),
  doi: z.string().optional(),
  arxivId: z.string().optional(),
  publicationDate: z.string().optional(),
  journal: z.string().optional(),
  conference: z.string().optional(),
  type: DocumentTypeEnum,
  pageCount: z.number().optional(),
  fileSize: z.number().optional(),
  language: z.string().optional(),
});
export type DocumentMeta = z.infer<typeof DocumentMetaSchema>;

export const AISummarySchema = z.object({
  brief: z.string(),
  detailed: z.string().optional(),
  keyFindings: z.array(z.string()),
  methodology: z.string().optional(),
  limitations: z.array(z.string()).optional(),
  generatedAt: z.string(),
});
export type AISummary = z.infer<typeof AISummarySchema>;

export const CitationSchema = z.object({
  id: z.string().uuid(),
  text: z.string(),
  authors: z.array(z.string()),
  year: z.string().optional(),
  title: z.string(),
  doi: z.string().optional(),
  isInLibrary: z.boolean(),
  linkedDocumentId: z.string().optional(),
});
export type Citation = z.infer<typeof CitationSchema>;

export const DocumentRelationshipSchema = z.object({
  targetId: z.string().uuid(),
  targetTitle: z.string(),
  type: z.enum(['cites', 'cited-by', 'related', 'extends', 'contradicts', 'reviews']),
  strength: z.number().min(0).max(1),
});
export type DocumentRelationship = z.infer<typeof DocumentRelationshipSchema>;

export const LibraryDocumentDTOSchema = z.object({
  id: z.string().uuid(),
  meta: DocumentMetaSchema,
  processingStatus: ProcessingStatusEnum,
  processingProgress: z.number().min(0).max(100).optional(),
  collections: z.array(z.string()),
  topics: z.array(z.string()),
  tags: z.array(z.string()),
  summary: AISummarySchema.optional(),
  citations: z.array(CitationSchema),
  relationships: z.array(DocumentRelationshipSchema),
  credibilityScore: z.number().min(0).max(100).optional(),
  source: z.string().optional(),
  hasFile: z.boolean().optional(),
  addedAt: z.string(),
  lastOpenedAt: z.string().optional(),
  readProgress: z.number().min(0).max(100).optional(),
  annotations: z.number().optional(),
  highlights: z.number().optional(),
  isStarred: z.boolean().optional(),
  isPaywalled: z.boolean().optional(),
  thumbnailUrl: z.string().optional(),
});
export type LibraryDocumentDTO = z.infer<typeof LibraryDocumentDTOSchema>;

export const LibraryCollectionDTOSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().optional(),
  color: CollectionColorEnum,
  icon: z.string().optional(),
  documentCount: z.number(),
  isSmartCollection: z.boolean(),
  smartQuery: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type LibraryCollectionDTO = z.infer<typeof LibraryCollectionDTOSchema>;

// ─── Request Types ─────────────────────────────────────────────────────

export interface LibraryListFilters {
  type?: DocumentType[];
  collections?: string[];
  topics?: string[];
  dateFrom?: string;
  dateTo?: string;
  processingStatus?: ProcessingStatus[];
  hasAnnotations?: boolean;
  readStatus?: 'unread' | 'in-progress' | 'complete';
  search?: string;
}

export type LibrarySortField = 'recent' | 'added' | 'title' | 'author' | 'citations' | 'relevance';

export interface LibraryListParams
  extends PaginationParams, SortParams<LibrarySortField>, LibraryListFilters {
  /** Workspace UUID — required by backend for tenant isolation. */
  workspaceId: string;
}

export interface LibraryCreateCollectionRequest {
  name: string;
  description?: string;
  color: CollectionColor;
  icon?: string;
  isSmartCollection?: boolean;
  smartQuery?: string;
}

export interface LibraryUpdateCollectionRequest {
  name?: string;
  description?: string;
  color?: CollectionColor;
  icon?: string;
  smartQuery?: string;
}

// ─── Response Types ────────────────────────────────────────────────────

export type LibraryListResponse = PaginatedResponse<LibraryDocumentDTO>;
export type LibraryDetailResponse = ApiResponse<LibraryDocumentDTO>;
export type LibraryCollectionListResponse = PaginatedResponse<LibraryCollectionDTO>;
export type LibraryCollectionDetailResponse = ApiResponse<LibraryCollectionDTO>;
export type LibraryCreateCollectionResponse = ApiResponse<LibraryCollectionDTO>;
export type LibraryUpdateCollectionResponse = ApiResponse<LibraryCollectionDTO>;
export type LibraryDeleteCollectionResponse = ApiResponse<{ id: string; deleted: boolean }>;

// ─── Adapter Interface ─────────────────────────────────────────────────

export interface ILibraryAdapter {
  list(params: LibraryListParams): Promise<LibraryListResponse>;
  getById(id: string): Promise<LibraryDetailResponse>;
  addToCollection(documentId: string, collectionId: string): Promise<void>;
  removeFromCollection(documentId: string, collectionId: string): Promise<void>;
  toggleStar(id: string): Promise<ApiResponse<{ id: string; isStarred: boolean }>>;
  listCollections(params: PaginationParams): Promise<LibraryCollectionListResponse>;
  getCollection(id: string): Promise<LibraryCollectionDetailResponse>;
  createCollection(data: LibraryCreateCollectionRequest): Promise<LibraryCreateCollectionResponse>;
  updateCollection(
    id: string,
    data: LibraryUpdateCollectionRequest,
  ): Promise<LibraryUpdateCollectionResponse>;
  deleteCollection(id: string): Promise<LibraryDeleteCollectionResponse>;
}
